import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter } from '../../../../components/ui/Dialog';
import Button from '../../../../components/ui/Button';
import BlockerPanel from './BlockerPanel';
import AuditLogViewer from './AuditLogViewer';
import MilestonePanel from './MilestonePanel';
import { searchSkills } from '../../../../api/Employee/skills';
import type { Goal, GoalDetailModalProps } from '../types';

interface SkillOption {
  id: number;
  name: string;
}

interface FormData {
  title: string;
  description: string;
  status: Goal['status'];
  progress: number;
  owner: string;
  ownerId?: string;
  department: string;
  departmentId?: number;
  startDate: string;
  dueDate: string;
  level: Goal['level'];
  parentId: string;
  metricType: number | null;
  skillIds: number[];
}

const METRIC_TYPE_OPTIONS = [
  { value: 1, labelKey: 'Percentage' },
  { value: 2, labelKey: 'Count' },
  { value: 3, labelKey: 'Time Duration' },
  { value: 4, labelKey: 'Quality Score' },
  { value: 5, labelKey: 'Revenue / Cost' },
  { value: 6, labelKey: 'Satisfaction Score' },
  { value: 7, labelKey: 'Other' },
];

const GoalDetailModal: React.FC<GoalDetailModalProps> = ({
  goal = null,
  isOpen = false,
  onClose = () => { },
  onSaveGoal = () => { },
  onDeleteGoal = () => { },
  allGoals = [],
  mode = 'view',
  departments = [],
  loadingDepartments = false,
  availableOwners = [],
  loadingOwners = false,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    status: 'on-track',
    progress: 0,
    owner: '',
    ownerId: undefined,
    department: 'engineering',
    departmentId: undefined,
    startDate: '',
    dueDate: '',
    level: 'individual',
    parentId: '',
    metricType: null,
    skillIds: [],
  });
  const [isEditing, setIsEditing] = useState<boolean>(mode === 'edit' || mode === 'create');
  const [activeTab, setActiveTab] = useState(0);
  const [availableSkills, setAvailableSkills] = useState<SkillOption[]>([]);
  const [knownSkills, setKnownSkills] = useState<Record<number, SkillOption>>({});
  const [loadingSkills, setLoadingSkills] = useState(false);
  const [skillSearchTerm, setSkillSearchTerm] = useState('');

  // Parse skill IDs from JSON string
  const parseSkillIds = (raw: string | null | undefined): number[] => {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map(Number).filter(n => !isNaN(n));
    } catch {
      // ignore
    }
    return [];
  };

  useEffect(() => {
    if (goal) {
      setFormData({
        title: goal.title || '',
        description: goal.description || '',
        status: goal.status || 'on-track',
        progress: goal.progress || 0,
        owner: goal.owner || '',
        ownerId: goal.ownerId,
        department: goal.department || 'engineering',
        departmentId: goal.departmentId,
        startDate: goal.startDate || '',
        dueDate: goal.dueDate || '',
        level: goal.level || 'individual',
        parentId: goal.parentId || '',
        metricType: goal.metricType ?? null,
        skillIds: parseSkillIds(goal.skillIds),
      });
    } else if (mode === 'create' && isOpen) {
      setFormData({
        title: '',
        description: '',
        status: 'on-track',
        progress: 0,
        owner: '',
        ownerId: undefined,
        department: 'engineering',
        departmentId: departments.length > 0 ? departments[0].id : 1,
        startDate: new Date().toISOString().split('T')[0],
        dueDate: new Date(new Date().setMonth(new Date().getMonth() + 3)).toISOString().split('T')[0],
        level: 'individual',
        parentId: '',
        metricType: null,
        skillIds: [],
      });
    }
    setIsEditing(mode === 'edit' || mode === 'create');
    setActiveTab(0);
  }, [goal, mode, isOpen, departments]);

  const statusOptions = [
    { value: 'on-track', label: t('On Track'), color: 'text-primary', icon: 'Target' },
    { value: 'at-risk', label: t('At Risk'), color: 'text-warning', icon: 'AlertTriangle' },
    { value: 'completed', label: t('Completed'), color: 'text-success', icon: 'CheckCircle' },
    { value: 'overdue', label: t('Overdue'), color: 'text-error', icon: 'XCircle' }
  ];

  const levelOptions = [
    { value: 'individual', label: t('Individual Goal') },
    { value: 'team', label: t('Team Goal') },
    { value: 'company', label: t('Company Goal') }
  ];

  const getMetricTypeLabel = (value: number | null): string => {
    if (value === null) return t('Not set');
    const option = METRIC_TYPE_OPTIONS.find(o => o.value === value);
    return option ? t(option.labelKey) : t('Unknown');
  };

  // Fetch skills when editing/creating and search term changes
  const fetchSkills = useCallback(async (term: string) => {
    try {
      setLoadingSkills(true);
      const result = await searchSkills(term || '');
      let mapped: SkillOption[] = [];
      if (result && result.payload && Array.isArray(result.payload)) {
        mapped = result.payload.map((s: { id: number; name: string }) => ({ id: s.id, name: s.name }));
      } else if (result && Array.isArray(result)) {
        mapped = (result as unknown as { id: number; name: string }[]).map(s => ({ id: s.id, name: s.name }));
      }
      setAvailableSkills(mapped);
      if (mapped.length > 0) {
        setKnownSkills(prev => {
          const next = { ...prev };
          mapped.forEach(s => { next[s.id] = s; });
          return next;
        });
      }
    } catch (error) {
      console.error('Failed to fetch skills:', error);
      setAvailableSkills([]);
    } finally {
      setLoadingSkills(false);
    }
  }, []);

  // Load skills when modal is open (needed for both view and edit to resolve skill names)
  useEffect(() => {
    if (isOpen) {
      fetchSkills(skillSearchTerm);
    }
  }, [isOpen, skillSearchTerm, fetchSkills]);

  const handleSkillToggle = (skillId: number) => {
    setFormData(prev => {
      const current = prev.skillIds;
      if (current.includes(skillId)) {
        return { ...prev, skillIds: current.filter(id => id !== skillId) };
      } else {
        return { ...prev, skillIds: [...current, skillId] };
      }
    });
  };

  const getParentGoalOptions = () => {
    if (formData.level === 'individual') {
      return allGoals.filter(g => g.level === 'team' && g.department === formData.department);
    } else if (formData.level === 'team') {
      return allGoals.filter(g => g.level === 'company');
    }
    return [];
  };

  const getFilteredOwners = () => {
    if (!formData.departmentId) {
      return availableOwners;
    }
    return availableOwners.filter(owner => owner.departmentId === formData.departmentId);
  };

  const handleOwnerChange = (ownerId: string) => {
    const selectedOwner = availableOwners.find(o => o.id === ownerId);
    if (selectedOwner) {
      setFormData(prev => ({
        ...prev,
        ownerId: selectedOwner.id,
        owner: selectedOwner.fullName,
      }));
    }
  };

  const handleInputChange = (field: keyof FormData, value: FormData[keyof FormData]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    const goalData: Goal = {
      ...formData,
      id: goal?.id || `${formData.level}-${Date.now()}`,
      milestones: goal?.milestones || [],
      metricType: formData.metricType,
      skillIds: formData.skillIds.length > 0 ? JSON.stringify(formData.skillIds) : null,
    };

    try {
      await onSaveGoal(goalData, mode === 'create');
      setIsEditing(false);
      onClose();
    } catch {
      // Don't close modal on error
    }
  };

  const handleDelete = () => {
    if (goal?.id && window.confirm(t('Are you sure you want to delete this goal?'))) {
      onDeleteGoal(goal.id);
      onClose();
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const parentGoalOptions = getParentGoalOptions();

  const handleClose = () => {
    setIsEditing(false);
    onClose();
  };

  if (!isOpen) return null;

  const tabLabels = [t('Details'), t('Milestones'), t('Blockers'), t('Audit Log')];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent size="xl" hideCloseButton>
        {/* Title */}
        <DialogHeader className="flex justify-between items-center py-3 px-4 flex-shrink-0">
          <div className="flex items-center space-x-3 rtl:space-x-reverse">
            <div className={`p-2 rounded-lg ${formData.level === 'company' ? 'bg-primary/10 text-primary' :
                formData.level === 'team' ? 'bg-accent/10 text-accent' : 'bg-secondary/10 text-secondary'
              }`}>
              <Icon name="Target" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                {mode === 'create' ? t('Create New Goal') : formData.title || t('Goal Details')}
              </h2>
              <p className="text-sm text-muted-foreground capitalize">
                {t('{{level}} Goal', { level: formData.level })}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2 rtl:space-x-reverse">
            {!isEditing && goal?.id && (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  className="p-2 hover:bg-muted rounded-lg transition-gentle"
                  title={t('Edit Goal')}
                >
                  <Icon name="Edit" size={20} className="text-muted-foreground" />
                </button>
                <button
                  onClick={handleDelete}
                  className="p-2 hover:bg-destructive/10 rounded-lg transition-gentle"
                  title={t('Delete Goal')}
                >
                  <Icon name="Trash2" size={20} className="text-destructive" />
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-gentle"
            >
              <Icon name="X" size={20} className="text-muted-foreground" />
            </button>
          </div>
        </DialogHeader>

        {/* Tabs - only show in view mode for existing goals */}
        {mode === 'view' && goal?.id && (
          <div className="flex border-b border-border px-4 flex-shrink-0">
            {tabLabels.map((label, i) => (
              <button
                key={label}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === i
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                }`}
                onClick={() => setActiveTab(i)}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <DialogBody className="overflow-y-auto flex-1">
          {/* Milestones Tab */}
          {activeTab === 1 && mode === 'view' && goal?.id && (
            <MilestonePanel
              goalId={goal.id}
              goalStartDate={goal.startDate}
              goalDueDate={goal.dueDate}
              isOpen={activeTab === 1}
              canEdit={true}
            />
          )}

          {/* Blockers Tab */}
          {activeTab === 2 && mode === 'view' && goal?.id && (
            <BlockerPanel goalId={goal.id} isOpen={activeTab === 2} canEdit={true} />
          )}

          {/* Audit Log Tab */}
          {activeTab === 3 && mode === 'view' && goal?.id && (
            <AuditLogViewer goalId={goal.id} isOpen={activeTab === 3} />
          )}

          {/* Details Tab (or edit/create mode) */}
          {(activeTab === 0 || mode !== 'view') && (
          <div className="p-6 space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                {isEditing ? (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('Goal Title')}
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      placeholder={t('Enter goal title...')}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                      required
                    />
                  </div>
                ) : (
                  <div>
                    <h3 className="font-medium text-foreground mb-2">{t('Goal Title')}</h3>
                    <p className="text-foreground">{formData.title}</p>
                  </div>
                )}
              </div>

              <div>
                {isEditing ? (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('Goal Level')}
                    </label>
                    <select
                      value={formData.level}
                      onChange={(e) => {
                        handleInputChange('level', e.target.value);
                        handleInputChange('parentId', '');
                      }}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                    >
                      {levelOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div>
                    <h3 className="font-medium text-foreground mb-2">{t('Level')}</h3>
                    <p className="text-foreground capitalize">{formData.level}</p>
                  </div>
                )}
              </div>

              <div>
                {isEditing ? (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('Department')} {loadingDepartments && t('(Loading...)')}
                    </label>
                   <select
  value={formData.departmentId || ''}
  onChange={(e) => {
    const selectedDeptId = e.target.value ? parseInt(e.target.value) : undefined;
    const selectedDept = departments.find(d => d.id === selectedDeptId);
    handleInputChange('departmentId', selectedDeptId);
    handleInputChange('department', selectedDept?.name || '');
  }}
  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
  disabled={loadingDepartments || departments.length === 0}
>
  <option value="">{t('Select Department (Optional)')}</option>
  {departments.map(dept => (
    <option key={dept.id} value={dept.id}>{dept.name}</option>
  ))}
  {!loadingDepartments && departments.length === 0 && (
    <option disabled value="">{t('No departments available')}</option>
  )}
</select>
                  </div>
                ) : (
                  <div>
                    <h3 className="font-medium text-foreground mb-2">{t('Department')}</h3>
                    <p className="text-foreground">{formData.department || t('Not assigned')}</p>
                  </div>
                )}
              </div>

              <div>
                {isEditing && mode === 'create' ? (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('Owner')} {loadingOwners && t('(Loading...)')}
                    </label>
                    <select
                      value={formData.ownerId || ''}
                      onChange={(e) => handleOwnerChange(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                      disabled={loadingOwners || availableOwners.length === 0}
                    >
                      <option value="">{t('Select Owner')}</option>
                      {getFilteredOwners().map(owner => (
                        <option key={owner.id} value={owner.id}>
                          {owner.fullName} {owner.departmentName ? `(${owner.departmentName})` : ''}
                        </option>
                      ))}
                      {!loadingOwners && getFilteredOwners().length === 0 && (
                        <option disabled value="">{t('No owners available for selected department')}</option>
                      )}
                    </select>
                    {getFilteredOwners().length > 0 && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('{{count}} owner(s) available', { count: getFilteredOwners().length })}
                        {formData.departmentId && ` ${t('for selected department')}`}
                      </p>
                    )}
                  </div>
                ) : isEditing && mode === 'edit' ? (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('Owner')}
                    </label>
                    <input
                      type="text"
                      value={formData.owner}
                      disabled
                      className="w-full px-3 py-2 border border-border rounded-lg bg-muted text-muted-foreground cursor-not-allowed"
                    />
                  </div>
                ) : (
                  <div>
                    <h3 className="font-medium text-foreground mb-2">{t('Owner')}</h3>
                    <p className="text-foreground">{formData.owner}</p>
                  </div>
                )}
              </div>

              <div>
                {isEditing ? (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('Progress')} ({formData.progress}%)
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={formData.progress}
                      onChange={(e) => handleInputChange('progress', parseInt(e.target.value))}
                      className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                ) : (
                  <div>
                    <h3 className="font-medium text-foreground mb-3">{t('Progress')}</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">{t('Completion')}</span>
                        <span className="text-lg font-semibold text-foreground">
                          {formData.progress}%
                        </span>
                      </div>
                      <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-primary to-secondary rounded-full transition-all duration-500"
                          style={{ width: `${formData.progress}%` }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            <div>
              {isEditing ? (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t('Description')}
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder={t('Enter goal description...')}
                    rows={4}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary resize-none"
                  />
                </div>
              ) : (
                <div>
                  <h3 className="font-medium text-foreground mb-2">{t('Description')}</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {formData.description}
                  </p>
                </div>
              )}
            </div>

            {/* Metric Type & Skills */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                {isEditing ? (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('Metric Type')}
                    </label>
                    <select
                      value={formData.metricType ?? ''}
                      onChange={(e) => {
                        const val = e.target.value ? parseInt(e.target.value) : null;
                        setFormData(prev => ({ ...prev, metricType: val }));
                      }}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                    >
                      <option value="">{t('Select Metric Type (Optional)')}</option>
                      {METRIC_TYPE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                      ))}
                    </select>
                    {formData.metricType === 7 && (
                      <div className="mt-2 flex items-center gap-2 text-sm text-warning bg-warning/10 p-2 rounded-lg">
                        <Icon name="AlertTriangle" size={16} className="text-warning flex-shrink-0" />
                        <span>{t('Consider choosing a specific metric type for better tracking and reporting.')}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <h3 className="font-medium text-foreground mb-2">{t('Metric Type')}</h3>
                    <p className="text-foreground">{getMetricTypeLabel(formData.metricType)}</p>
                  </div>
                )}
              </div>

              <div>
                {isEditing ? (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('Linked Skills')} {loadingSkills && t('(Loading...)')}
                    </label>
                    <input
                      type="text"
                      value={skillSearchTerm}
                      onChange={(e) => setSkillSearchTerm(e.target.value)}
                      placeholder={t('Search skills...')}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary mb-2"
                    />
                    {formData.skillIds.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {formData.skillIds.map(skillId => {
                          const skill = availableSkills.find(s => s.id === skillId) || knownSkills[skillId];
                          return (
                            <span
                              key={skillId}
                              className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full"
                            >
                              {skill?.name || `#${skillId}`}
                              <button
                                type="button"
                                onClick={() => handleSkillToggle(skillId)}
                                className="hover:text-destructive transition-colors"
                              >
                                <Icon name="X" size={12} />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                    <div className="max-h-32 overflow-y-auto border border-border rounded-lg">
                      {availableSkills.length === 0 && !loadingSkills && (
                        <p className="p-2 text-sm text-muted-foreground text-center">{t('No skills found')}</p>
                      )}
                      {availableSkills.map(skill => (
                        <label
                          key={skill.id}
                          className="flex items-center gap-2 px-3 py-1.5 hover:bg-muted/50 cursor-pointer text-sm"
                        >
                          <input
                            type="checkbox"
                            checked={formData.skillIds.includes(skill.id)}
                            onChange={() => handleSkillToggle(skill.id)}
                            className="rounded border-border text-primary focus:ring-primary/20"
                          />
                          <span className="text-foreground">{skill.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div>
                    <h3 className="font-medium text-foreground mb-2">{t('Linked Skills')}</h3>
                    {formData.skillIds.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {formData.skillIds.map(skillId => {
                          const skill = availableSkills.find(s => s.id === skillId) || knownSkills[skillId];
                          return (
                            <span
                              key={skillId}
                              className="inline-flex items-center px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full"
                            >
                              {skill?.name || `#${skillId}`}
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-muted-foreground">{t('None')}</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                {isEditing ? (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('Start Date')}
                    </label>
                    <input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => handleInputChange('startDate', e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                    />
                  </div>
                ) : (
                  <div>
                    <h3 className="font-medium text-foreground mb-2">{t('Start Date')}</h3>
                    <p className="text-foreground">{formatDate(formData.startDate)}</p>
                  </div>
                )}
              </div>

              <div>
                {isEditing ? (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('Due Date')}
                    </label>
                    <input
                      type="date"
                      value={formData.dueDate}
                      onChange={(e) => handleInputChange('dueDate', e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                    />
                  </div>
                ) : (
                  <div>
                    <h3 className="font-medium text-foreground mb-2">{t('Due Date')}</h3>
                    <p className="text-foreground">{formatDate(formData.dueDate)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Parent Goal Linking */}
            {parentGoalOptions.length > 0 && (
              <div>
                {isEditing ? (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('Link to {{level}} Goal', { level: formData.level === 'individual' ? t('Team') : t('Company') })}
                    </label>
                    <select
                      value={formData.parentId}
                      onChange={(e) => handleInputChange('parentId', e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                    >
                      <option value="">{t('No Parent Goal')}</option>
                      {parentGoalOptions.map(g => (
                        <option key={g.id} value={g.id}>{g.title}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div>
                    <h3 className="font-medium text-foreground mb-2">
                      {t('Linked {{level}} Goal', { level: formData.level === 'individual' ? t('Team') : t('Company') })}
                    </h3>
                    <p className="text-foreground">
                      {allGoals.find(g => g.id === formData.parentId)?.title || t('None')}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Status */}
            <div>
              <h3 className="font-medium text-foreground mb-3">
                {isEditing ? t('Update Status') : t('Current Status')}
              </h3>
              {isEditing ? (
                <div className="grid grid-cols-2 gap-3">
                  {statusOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleInputChange('status', option.value)}
                      className={`flex items-center space-x-2 rtl:space-x-reverse p-3 rounded-lg border-2 transition-gentle ${formData.status === option.value
                          ? 'border-primary bg-primary/10' : 'border-border hover:border-muted-foreground'
                        }`}
                    >
                      <Icon name={option.icon} size={16} className={option.color} />
                      <span className="text-sm font-medium text-foreground">
                        {option.label}
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  {statusOptions.map((option) =>
                    option.value === formData.status && (
                      <div key={option.value} className="flex items-center space-x-2 rtl:space-x-reverse">
                        <Icon name={option.icon} size={16} className={option.color} />
                        <span className="font-medium text-foreground">{option.label}</span>
                      </div>
                    )
                  )}
                </div>
              )}
            </div>

            {/* Milestone Summary */}
            {goal?.milestones && goal.milestones.length > 0 && !isEditing && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-foreground">{t('Key Milestones')}</h3>
                  <button
                    onClick={() => setActiveTab(1)}
                    className="text-xs text-primary hover:text-primary/80 transition-colors"
                    data-testid="view-all-milestones-btn"
                  >
                    {t('View All')} →
                  </button>
                </div>
                <div className="space-y-2">
                  {goal.milestones.slice(0, 3).map((milestone, index) => (
                    <div key={index} className="flex items-center space-x-3 rtl:space-x-reverse p-2 bg-muted/50 rounded-lg">
                      <Icon
                        name={milestone.completed ? "CheckCircle" : "Circle"}
                        size={16}
                        className={milestone.completed ? "text-success" : "text-muted-foreground"}
                      />
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${milestone.completed ? 'text-foreground' : 'text-muted-foreground'}`}>
                          {milestone.title}
                        </p>
                      </div>
                    </div>
                  ))}
                  {goal.milestones.length > 3 && (
                    <p className="text-xs text-muted-foreground text-center">
                      {t('+{{count}} more', { count: goal.milestones.length - 3 })}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
          )}
        </DialogBody>

        {/* Actions */}
        <DialogFooter className="px-4 py-3 flex-shrink-0">
          <div className="flex items-center space-x-3 rtl:space-x-reverse">
            {isEditing ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsEditing(false);
                    if (mode === 'create') onClose();
                  }}
                >
                  {t('Cancel')}
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={!formData.title.trim()}
                >
                  {mode === 'create' ? t('Create Goal') : t('Save Changes')}
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={onClose}>
                {t('Close')}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GoalDetailModal;
